import type { PayPeriod } from '@/common/hooks/useTimesheets'

/**
 * Returns the two allowed periods (previous + current) for non-admin users,
 * or all periods for admins.
 *
 * "Previous" = most recent period where end_date < today (state is active or closed).
 * "Current"  = the period containing today (state is drafting).
 */
export function getAllowedPeriods(
  periods: PayPeriod[],
  roles: string[],
): { previous: PayPeriod | null; current: PayPeriod | null; isRestricted: boolean } {
  const isAdmin = roles.includes('admin')

  if (isAdmin) {
    return { previous: null, current: null, isRestricted: false }
  }

  const sorted = [...periods].sort((a, b) => a.start_date.localeCompare(b.start_date))

  // Current = first drafting period (the one containing today)
  // Previous = the period immediately before current in sorted order
  const currentIndex = sorted.findIndex((p) => p.state === 'drafting')
  const current = currentIndex >= 0 ? sorted[currentIndex] : null
  const previous = currentIndex > 0 ? sorted[currentIndex - 1] : null

  return { previous, current, isRestricted: true }
}

/**
 * Returns the period that should be shown by default.
 *
 * - Previous period when its state is 'active' (submission still open)
 * - Current period when previous is 'closed' or missing
 */
export function getDefaultPeriod(
  previous: PayPeriod | null,
  current: PayPeriod | null,
): PayPeriod | null {
  if (previous?.state === 'active') return previous
  if (current) return current
  return previous
}
